import React from "react";
import LegalPage from "../../components/LegalPage";

export default function RefundPolicy() {
  return (
    <LegalPage
      testid="legal-refunds"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Refund and Cancellation Policy"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="This Refund and Cancellation Policy forms part of our Terms of Service and explains when and how you may cancel an order or request a refund for an eSIM data plan (a “Data Plan”) purchased from BlueRoam Ltd (“BlueRoam”, “we”, “us”) via blueroam.io. This Policy applies to customers worldwide and is supplemented by mandatory consumer-protection laws in your country of residence."
    >
      <h2>1. Summary at a Glance</h2>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left p-3 border-b border-ink-200">Situation</th>
              <th className="text-left p-3 border-b border-ink-200 whitespace-nowrap">Refund?</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200">Order placed but <strong>not yet activated</strong>, request received within 14 days</td><td className="p-3 border-b border-ink-200">✅ Yes — full refund</td></tr>
            <tr><td className="p-3 border-b border-ink-200">eSIM <strong>activated</strong> or already in use</td><td className="p-3 border-b border-ink-200">❌ No — except where the Services are faulty (see §4)</td></tr>
            <tr><td className="p-3 border-b border-ink-200">eSIM <strong>incompatible with your device</strong></td><td className="p-3 border-b border-ink-200">❌ No — verify compatibility before purchase (see §5)</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Faulty, not as described, or technical failure on our side</td><td className="p-3 border-b border-ink-200">✅ Yes — remedy in line with your statutory rights</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Partial or unused data after expiry</td><td className="p-3 border-b border-ink-200">❌ No — data does not roll over (unless the Data Plan listing says otherwise)</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Wrong country/region selected at checkout (and unactivated)</td><td className="p-3 border-b border-ink-200">✅ Yes — full refund within 14 days</td></tr>
            <tr><td className="p-3">Fraudulent or unauthorised purchase</td><td className="p-3">✅ Yes — full refund and account review</td></tr>
          </tbody>
        </table>
      </div>
      <p>The detail below explains how each situation is treated, including jurisdiction-specific rights.</p>

      <h2>2. Statutory Cancellation Rights</h2>
      <h3>2.1 UK Consumers — Cooling-Off Period</h3>
      <p>
        If you are a <strong>UK consumer</strong> purchasing for purposes outside your trade,
        business, craft or profession, you have a statutory right under the Consumer Contracts
        (Information, Cancellation and Additional Charges) Regulations 2013 to cancel a distance
        contract within <strong>14 days</strong> from the day after the contract is concluded,
        without giving any reason.
      </p>
      <h3>2.2 EU/EEA Consumers — Cooling-Off Period</h3>
      <p>
        If you are an <strong>EU or EEA consumer</strong>, you have an equivalent{" "}
        <strong>14-day</strong> right of withdrawal under Directive 2011/83/EU on Consumer Rights,
        as implemented in your country of residence.
      </p>
      <h3>2.3 Digital Content Waiver (UK and EU/EEA)</h3>
      <p>
        Under both the UK regulations referenced in clause 2.1 and the EU Consumer Rights Directive,
        the right to cancel is <strong>lost</strong> in relation to “the supply of digital content
        not on a tangible medium” once supply has begun, where:
      </p>
      <ul>
        <li>(a) you gave <strong>express prior consent</strong> for supply to begin within the 14-day cancellation period; <strong>and</strong></li>
        <li>(b) you <strong>acknowledged</strong> that you would lose your right to cancel once supply began.</li>
      </ul>
      <p>
        By selecting “<em>I agree to the supply of my eSIM immediately and acknowledge that I lose
        my right to cancel once supply begins</em>” (or substantively equivalent wording) at
        checkout, you provide that consent and acknowledgment.
      </p>
      <h3>2.4 What This Means in Practice</h3>
      <ul>
        <li><strong>Before delivery of your QR code / activation profile:</strong> cancel and obtain a full refund.</li>
        <li><strong>After delivery but before activation:</strong> request a refund within 14 days; we will normally grant it as a matter of goodwill provided the eSIM has not been activated.</li>
        <li><strong>After activation:</strong> statutory cancellation right has been waived per clause 2.3, and the eSIM is non-refundable except for faults or non-conformity.</li>
      </ul>
      <h3>2.5 Consumers Outside the UK and EU/EEA</h3>
      <p>
        If you live elsewhere, you may have similar rights under local law (e.g., the{" "}
        <strong>Australian Consumer Law</strong> under the Competition and Consumer Act 2010,{" "}
        <strong>Canada’s</strong> provincial consumer-protection statutes,{" "}
        <strong>Brazil’s</strong> Consumer Defence Code, <strong>Japan’s</strong> Specified
        Commercial Transactions Act, <strong>Singapore’s</strong> Consumer Protection (Fair Trading)
        Act, etc.). Where local law gives you stronger rights than this Policy,{" "}
        <strong>the local rights prevail</strong>.
      </p>

      <h2>3. How to Cancel or Request a Refund</h2>
      <p>3.1 To exercise a cancellation or refund right, inform us by a clear statement:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:hello@blueroam.io">hello@blueroam.io</a></li>
        <li><strong>In-Platform:</strong> via your account</li>
        <li><strong>Post:</strong> BlueRoam Ltd, 128 City Road, London, EC1V 2NX, United Kingdom</li>
      </ul>
      <p>3.2 You may use the model cancellation form in <strong>Annex A</strong> but are not obliged to.</p>
      <p>3.3 We acknowledge requests without undue delay and confirm whether they are accepted.</p>

      <h2>4. Faulty, Misdescribed or Non-Conforming Services</h2>
      <p>
        4.1 Under the UK Consumer Rights Act 2015, the EU Digital Content Directive and equivalent
        consumer-protection laws worldwide, the Services must be:
      </p>
      <ul>
        <li>(a) of satisfactory quality;</li>
        <li>(b) fit for any particular purpose made known to us; and</li>
        <li>(c) as described.</li>
      </ul>
      <p>4.2 If the Services fail to meet these standards, you may be entitled to:</p>
      <ul>
        <li><strong>Repair or replacement</strong> (e.g., reissuing the eSIM profile);</li>
        <li><strong>A price reduction</strong> for partially functional service;</li>
        <li><strong>A full or partial refund</strong>, in line with applicable consumer law.</li>
      </ul>
      <p>
        4.3 To make a claim:{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> with your order number, device
        make/model and operating system, destination country/region, a description of the issue
        (dates, times, locations), and any screenshots, signal-strength data or error messages.
      </p>
      <p>
        4.4 We investigate in good faith. Where the issue is verified and within our control or that
        of our Partner Networks, we provide a remedy. Where it is not verified, or is caused by
        factors outside our control (clause 6), we may decline the refund but will explain our
        reasoning.
      </p>

      <h2>5. Device Compatibility</h2>
      <p>5.1 You are responsible for verifying that your device:</p>
      <ul>
        <li>(a) supports eSIM technology;</li>
        <li>(b) is not network-locked to a specific carrier; and</li>
        <li>(c) is compatible with the destination country’s mobile networks and bands.</li>
      </ul>
      <p>
        5.2 Compatibility information is provided before purchase.{" "}
        <strong>eSIMs purchased for incompatible devices are not refundable</strong>, except where
        required by applicable consumer law.
      </p>

      <h2>6. Excluded Situations</h2>
      <p>The following are <strong>not</strong> grounds for a refund:</p>
      <ul>
        <li>(a) coverage limitations in remote, indoor, underground or low-signal locations consistent with published Partner Network coverage;</li>
        <li>(b) speed variations within the technical parameters of the relevant network technology;</li>
        <li>(c) data depletion through ordinary use, including data consumed by your device’s OS and apps;</li>
        <li>(d) failure to install or activate the eSIM in accordance with instructions;</li>
        <li>(e) loss or theft of your device after the eSIM is installed;</li>
        <li>(f) <strong>restrictions imposed by local law</strong> in the destination country (content blocks, VoIP restrictions, internet shutdowns);</li>
        <li>(g) where you have used more than a <strong>de minimis</strong> amount of data on the eSIM (we generally treat any data usage above <strong>[50 MB] as a deemed activation</strong>, subject to clause 4);</li>
        <li>(h) chargebacks or refund requests submitted more than <strong>180 days</strong> after purchase, save where required by law.</li>
      </ul>

      <h2>7. How and When Refunds Are Paid</h2>
      <p>
        7.1 Approved refunds are paid to the original payment method used for the purchase. We may
        use a different method only with your express agreement and only if it does not cause you
        to incur fees.
      </p>
      <p>
        7.2 We process approved refunds within <strong>14 days</strong> of accepting your request.
        Time for funds to appear in your account depends on your card issuer or payment provider.
      </p>
      <p>7.3 Refunds are made in the original transaction currency. We are not responsible for FX fluctuations between purchase and refund.</p>

      <h2>8. Promotional Codes, Vouchers and Bundles</h2>
      <p>8.1 Refunds for purchases made with promotional discounts are limited to the amount <strong>actually paid</strong> (not the headline price).</p>
      <p>8.2 Refunds on partially used bundles are calculated pro-rata for unactivated components only.</p>

      <h2>9. Chargebacks</h2>
      <p>9.1 If you raise a chargeback with your card issuer, please also notify <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> so we can resolve it promptly.</p>
      <p>9.2 We may suspend or terminate accounts associated with chargebacks made in bad faith and recover reasonable costs of defending such chargebacks.</p>

      <h2>10. Business Customers</h2>
      <p>
        If you purchase as a business (not as a Consumer), the statutory rights in clauses 2 and 4
        do not apply. Refunds for business customers are at BlueRoam’s discretion, save as set out
        in any applicable business terms.
      </p>

      <h2>11. Contact and Complaints</h2>
      <p>11.1 Escalations: <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.</p>
      <p>11.2 If we cannot resolve a complaint, see clause 17 of the <a href="/legal/terms">Terms of Service</a> for dispute-resolution options applicable to your country.</p>

      <h2>Annex A — Model Cancellation Form</h2>
      <p><em>(Complete and return only if you wish to cancel.)</em></p>
      <p>To: BlueRoam Ltd, 128 City Road, London, EC1V 2NX, United Kingdom — <a href="mailto:hello@blueroam.io">hello@blueroam.io</a></p>
      <p>I/We [*] hereby give notice that I/We [*] cancel my/our [*] contract of sale of the following Service:</p>
      <ul>
        <li>Order number: ______________________</li>
        <li>Ordered on / received on [*]: ______________________</li>
        <li>Name of consumer(s): ______________________</li>
        <li>Address of consumer(s): ______________________</li>
        <li>Signature of consumer(s) (only if this form is notified on paper): ______________________</li>
        <li>Date: ______________________</li>
      </ul>
      <p className="text-xs text-ink-500">[*] <em>Delete as appropriate.</em></p>
    </LegalPage>
  );
}
